import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { LoadingSpinner } from './LoadingSpinner';

export type AppConfirmDialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Roter Bestätigen-Button (z. B. Löschen). */
  destructive?: boolean;
  /** Amber warning chrome instead of a neutral confirm. */
  tone?: 'default' | 'warning';
  isLoading?: boolean;
  /** Bestätigen-Button sperren (z. B. solange eine Pflichtauswahl fehlt). */
  confirmDisabled?: boolean;
  overlayClassName?: string;
  onConfirm: () => void | Promise<void>;
  /**
   * Eigene Aktion für den Abbrechen-Button (z. B. „Nein, nur …“). Schließen über X, Escape und
   * Overlay ruft weiterhin `onClose` — das bleibt der folgenlose Ausstieg.
   */
  onCancel?: () => void;
};

export function AppConfirmDialog({
  open,
  onClose,
  title,
  description,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  destructive = false,
  tone = 'default',
  isLoading = false,
  confirmDisabled = false,
  overlayClassName = 'z-[100]',
  onConfirm,
  onCancel,
}: AppConfirmDialogProps) {
  const overlayMouseDownRef = React.useRef(false);
  const isWarning = tone === 'warning';

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) onClose();
    };
    const resetOverlayPress = () => {
      overlayMouseDownRef.current = false;
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mouseup', resetOverlayPress);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mouseup', resetOverlayPress);
    };
  }, [open, isLoading, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={`fixed inset-0 ${overlayClassName} flex items-center justify-center bg-slate-900/45 p-4`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-confirm-dialog-title"
      onMouseDown={(event) => {
        overlayMouseDownRef.current = event.target === event.currentTarget;
      }}
      onMouseUp={(event) => {
        if (isLoading) return;
        if (event.target === event.currentTarget && overlayMouseDownRef.current) onClose();
      }}
    >
      <div
        className={`relative w-full max-w-md overflow-hidden rounded-xl bg-app-surface shadow-2xl border ${
          isWarning ? 'border-amber-200 dark:border-amber-800' : 'border-app-border'
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        {isWarning ? (
          <>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-amber-900 hover:bg-amber-100 hover:text-amber-950 disabled:opacity-40"
              aria-label="Schließen"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="border-b border-amber-100 bg-amber-50 px-5 py-4 pr-12">
              <h2 id="app-confirm-dialog-title" className="text-base font-semibold text-amber-950">
                {title}
              </h2>
              {description != null ? (
                <div className="mt-2 text-sm leading-relaxed text-amber-950">{description}</div>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 px-5 py-4">
              <h2 id="app-confirm-dialog-title" className="text-sm font-semibold text-app-text">
                {title}
              </h2>
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-lg text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted disabled:opacity-40"
                aria-label="Schließen"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {description != null ? (
              <div className="px-5 pb-5 text-sm text-app-text-muted leading-relaxed">{description}</div>
            ) : null}
          </>
        )}

        <div className={`flex items-center justify-end gap-2 px-5 ${isWarning ? 'py-4' : 'pb-4'}`}>
          <button
            type="button"
            onClick={onCancel ?? onClose}
            disabled={isLoading}
            className="inline-flex h-9 items-center justify-center rounded-md border border-app-border bg-app-surface px-3.5 text-sm font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-40 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            disabled={isLoading || confirmDisabled}
            onClick={() => void Promise.resolve(onConfirm())}
            className={`inline-flex h-9 items-center justify-center gap-2 rounded-md px-3.5 text-sm font-semibold text-white disabled:opacity-40 transition-colors ${
              destructive
                ? 'bg-red-600 hover:bg-red-700'
                : isWarning
                  ? 'bg-amber-600 hover:bg-amber-700'
                  : 'bg-pipedrive-blue hover:bg-blue-700'
            }`}
          >
            {isLoading ? <LoadingSpinner size="sm" /> : null}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
